import { DataProvider, GetListParams, QueryFunctionContext } from "react-admin";
import voucherProvider from "./vouchersProvider";
import gameProvider from "./gamesProvider";
import entityProvider from "./entitiesProvider";
import citiesProvider from "./citiesProvider";
import locationProvider from "./locationsProvider";
import statProvider, { StatProvider } from "./statProvider";
import {
  HistoryVoucherType,
  RankingType,
  StatsOverviewType,
  StatsOverviewVoucherType,
  Voucher,
} from "@/types";
import claimProvider from "./claimProvider";

type Resource =
  | "games"
  | "vouchers"
  | "entities"
  | "cities"
  | "locations"
  | "stats";

type ExtendedDataProvider = DataProvider & {
  getStatsOverview: () => Promise<{ data: StatsOverviewType }>;
  getStatsOverviewVoucher: () => Promise<{ data: StatsOverviewVoucherType }>;
  claimVoucher: (args: { code: string }) => Promise<{ data: Voucher }>;
  getHistoryVoucher: () => Promise<{ data: HistoryVoucherType[] }>;
  getRanking: (type: "day" | "week") => Promise<{ data: RankingType[] }>;
};

const providersMap: Record<Resource, Partial<DataProvider>> = {
  games: gameProvider,
  vouchers: voucherProvider,
  entities: entityProvider,
  cities: citiesProvider,
  locations: locationProvider,
  stats: statProvider,
};

const ensureProviderMethodExists = (
  provider: Partial<DataProvider>,
  methodName: keyof DataProvider,
  resource: string
): void => {
  if (!(methodName in provider)) {
    throw new Error(
      `Unknown resource or missing method: ${resource} -> ${methodName}`
    );
  }
};

const callProviderMethod = async (
  methodName: keyof DataProvider,
  resource: string,
  params: GetListParams & QueryFunctionContext
) => {
  try {
    const provider = providersMap[resource as keyof typeof providersMap];
    ensureProviderMethodExists(provider, methodName, resource);
    return provider[methodName](resource, params);
  } catch (error) {
    console.error("DataProvider error:", error);
    throw error;
  }
};

const dataProvider: ExtendedDataProvider = {
  getList: async (resource, params) => {
    return callProviderMethod("getList", resource, params);
  },

  getOne: async (resource, params) =>
    callProviderMethod("getOne", resource, params),

  create: async (resource, params) =>
    callProviderMethod("create", resource, params),

  update: async (resource, params) =>
    callProviderMethod("update", resource, params),

  delete: async (resource, params) =>
    callProviderMethod("delete", resource, params),

  getMany: async (resource, params) =>
    callProviderMethod("getMany", resource, params),

  getManyReference: async (resource, params) =>
    callProviderMethod("getManyReference", resource, params),

  updateMany: async (resource, params) =>
    callProviderMethod("updateMany", resource, params),

  deleteMany: async (resource, params) =>
    callProviderMethod("deleteMany", resource, params),

  // 👇 Add the custom function
  async getStatsOverview() {
    const p = providersMap["stats"] as StatProvider;
    if (!p?.getStatsOverview) {
      throw new Error("Unknown dataProvider function: getStatsOverview");
    }
    return p.getStatsOverview();
  },

  async getStatsOverviewVoucher() {
    const p = providersMap["stats"] as StatProvider;
    if (!p?.getStatsOverviewVoucher) {
      throw new Error("Unknown dataProvider function: getStatsOverviewVoucher");
    }
    return p.getStatsOverviewVoucher();
  },

  async claimVoucher({ code }) {
    return claimProvider.claimVoucher({ code });
  },

  async getHistoryVoucher() {
    const p = providersMap["stats"] as StatProvider;
    if (!p?.getHistoryVoucher) {
      throw new Error("Unknown dataProvider function: getHistoryVoucher");
    }
    return p.getHistoryVoucher();
  },

  async getRanking(type: "day" | "week") {
    const p = providersMap["stats"] as StatProvider;
    if (!p?.getRanking) {
      throw new Error("Unknown dataProvider function: getRanking");
    }
    return p.getRanking(type);
  },
};

export { dataProvider };
